import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { FolderKanban } from 'lucide-react';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormSelectField,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import { questionClassesQueryOptions } from '../api/question.api';
import { useSaveQuestionProject } from '../hooks/useQuestionMutations';
import {
  questionProjectFormSchema,
  type QuestionProject,
  type QuestionProjectFormValues,
} from '../types/question.types';

const DEFAULTS: QuestionProjectFormValues = {
  name: '',
  isActive: true,
};

const toDateInput = (value: string | null): string | undefined => {
  if (value === null) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(
        parsed.getDate(),
      ).padStart(2, '0')}`;
};

interface QuestionProjectFormModalProps {
  project: QuestionProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuestionProjectFormModal({
  project,
  open,
  onOpenChange,
}: QuestionProjectFormModalProps) {
  const { data: classes, isLoading: classesLoading } = useQuery(questionClassesQueryOptions());

  const form = useForm<QuestionProjectFormValues>({
    resolver: zodResolver(questionProjectFormSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    form.reset(
      project === null
        ? DEFAULTS
        : {
            name: project.name,
            isActive: project.isActive,
            ...(project.classId === null ? {} : { classId: project.classId }),
            ...(toDateInput(project.declareDate) === undefined
              ? {}
              : { declareDate: toDateInput(project.declareDate) }),
            ...(toDateInput(project.deadlineDate) === undefined
              ? {}
              : { deadlineDate: toDateInput(project.deadlineDate) }),
          },
    );
  }, [project, open, form]);

  const save = useSaveQuestionProject(() => onOpenChange(false));

  const classOptions = (classes ?? []).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  function submit(values: QuestionProjectFormValues) {
    save.mutate({
      id: project?.projectId ?? null,
      payload: {
        name: values.name,
        isActive: values.isActive,
        ...(values.classId === undefined ? {} : { classId: values.classId }),
        ...(values.declareDate === undefined ? {} : { declareDate: values.declareDate }),
        ...(values.deadlineDate === undefined ? {} : { deadlineDate: values.deadlineDate }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={FolderKanban}
      title={project === null ? 'Add Project' : 'Edit Project'}
      description="A collection round. Leave a date empty to clear it."
      form={form}
      isPending={save.isPending}
      isEditMode={project !== null}
      onSubmit={submit}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="SSC Board Question Collection-2026"
          isFullRow
        />
        <FormSelectField
          control={form.control}
          name="classId"
          label="Class"
          options={classOptions}
          isLoading={classesLoading}
          placeholder="Pick a class"
          isFullRow
        />
        <FormDateField control={form.control} name="declareDate" label="Declared" isOptional />
        <FormDateField control={form.control} name="deadlineDate" label="Deadline" isOptional />
      </FormSection>
      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}
